import { settingApi } from '../../../slice/settingApi';

import type { listParamsType } from '../../../slice/types';

type teamParamsType = {
  ProfileURL?: string;
  id?: string;
  name?: string;
  users?: [] | string[];
  userId?: [] | string[];
  status?: string;
  leadId?: string | null;
  memberIds?: string[];
};

interface IUserItemResponse {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  status: string;
  createdAt: string;
}

interface ITeamItemResponse {
  id: string;
  name: string;
  users: IUserItemResponse[] | [];
  leadId?: string | null;
  lead?: IUserItemResponse | null;
  createdAt: string;
  status: string;
}

const TEAM_API = {
  list: 'api/teams/list',
  create: 'api/teams',
  update: 'api/teams',
  delete: 'api/teams',
  details: 'api/teams/details',
};

export const teamApi = settingApi.injectEndpoints({
  endpoints: (builder) => ({
    // Filter Team List => Search, Sort, Filter
    filterTeam: builder.mutation({
      query: ({ filters, globalSearch, page = 1, limit = 10 }: listParamsType) => ({
        url: TEAM_API.list,
        method: 'POST',
        body: { filters, globalSearch, page, limit },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: { list?: ITeamItemResponse[]; total?: number } }) => {
        const teamList = response?.data?.list?.map((item: any) => ({
          id: item.id ?? '',
          teamName: item.name ?? '',
          noOfUsers: item.users?.length ?? 0,
          leadId: item.leadId ?? null,
          lead: item.lead ? {
            id: item.lead.id,
            firstName: item.lead.firstName ?? '',
            lastName: item.lead.lastName ?? '',
            email: item.lead.email ?? '',
          } : null,
          users: (item.users ?? []).map((user: any) => ({
            id: user.user?.id || user.id || '',
            username: `${user.user?.firstName ?? user.firstName ?? ''} ${user.user?.lastName ?? user.lastName ?? ''}`.trim(),
            firstName: user.user?.firstName ?? user.firstName ?? '',
            lastName: user.user?.lastName ?? user.lastName ?? '',
            email: user.user?.email ?? user.email ?? '',
            role: user.user?.role ?? user.role ?? '',
            joinedDate: user.user?.createdAt ?? user.createdAt ?? '',
            status: user.user?.status ?? user.status ?? 'INACTIVE',
            ProfileURL: user.user?.ProfileURL ?? user.ProfileURL ?? '',
          })),
          createdDate: item.createdAt ?? '',
          updatedAt: item.createdAt ?? '',
          status: item.status,
        }));
        return { list: teamList ?? [], total: response?.data?.total ?? 0 };
      },
    }),

    // Get Team Details
    getTeamDtl: builder.mutation({
      query: ({ id }: teamParamsType) => ({
        url: `${TEAM_API.details}/${id}`,
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
      transformResponse: (response: { data?: any }) => response?.data,
    }),

    // Create Team
    createTeam: builder.mutation({
      query: ({ name, users, status, leadId, memberIds }: teamParamsType) => ({
        url: TEAM_API.create,
        method: 'POST',
        body: {
          name,
          status,
          leadId: leadId && String(leadId).trim() !== '' ? String(leadId).trim() : null,
          memberIds: memberIds || users,
          userId: users,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // Update Team
    updateTeam: builder.mutation({
      query: ({ id, name, users, status, leadId, memberIds, ProfileURL }: teamParamsType) => ({
        url: `${TEAM_API.update}/${id}`,
        method: 'PUT',
        body: {
          id,
          teamId: id,
          name,
          status,
          leadId: leadId && String(leadId).trim() !== '' ? String(leadId).trim() : null,
          memberIds: memberIds || users,
          userId: users,
          ProfileURL,
        },
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),

    // Delete Team
    deleteTeam: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `${TEAM_API.delete}/${id}`,
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
  }),
});

export const {
  useFilterTeamMutation,
  useGetTeamDtlMutation,
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
} = teamApi;
